import type { Translations } from './types';

const es: Translations = {
  locale: 'es-MX',

  shared: {
    continue: 'Continuar',
    otherNotSure: 'Otro/No estoy seguro/a',
  },

  gold: {
    needHelp: '¿Necesita ayuda?',
    karatHelp: 'El valor en quilates generalmente está grabado en su joya como 10K, 14K, 18K o 24K.',
    karatRingsBold: 'anillos',
    karatRingsRest: ', revise el interior de la banda.',
    karatNecklacesBold: 'collares o pulseras',
    karatNecklacesRest: ', busque cerca del cierre.',
    karatEarringsBold: 'aretes',
    karatEarringsRest: ', puede estar en el poste o en la parte trasera.',
    purityHelp: 'El valor de pureza generalmente está grabado en su pieza. Busque sellos como:',
    alsoKnownAs: ' también conocido como ',
    or: ' o ',
    additionalComments: 'Comentarios adicionales (opcional)',
  },

  itemCard: {
    estimatedValue: 'Valor estimado',
    internalStorage: 'Almacenamiento interno',
    purity: 'Pureza',
    condition: 'Condición',
    remove: 'Eliminar',
    edit: 'Editar',
    storeCardOtherMessage: (attrs) =>
      `Necesitamos ${attrs} de su artículo para proporcionarle un valor estimado.\n\nTambién puede traer su artículo a una de nuestras tiendas para una revisión en persona.`,
    storeCardDefaultMessage:
      'Deberá traer este artículo a una de nuestras tiendas para una revisión en persona.',
  },

  otp: {
    almostThere: '¡Ya casi!',
    quotesDeliveredByText:
      'Las cotizaciones se entregan por mensaje de texto. Para recibir su cotización, proporcione un número de teléfono a continuación.',
    customerName: 'Nombre',
    phoneNumber: 'Número de teléfono',
    phonePlaceholder: '123 202 2020',
    phoneInfo: 'Le enviaremos su estimación de cotización y número de referencia por texto.',
    zipCode: 'Código ZIP',
    zipPlaceholder: '78109',
    zipTooltip: 'Su código ZIP nos ayuda a sugerir tiendas locales.',
    phoneError: 'Por favor, ingrese un número de teléfono válido de 10 dígitos.',
    zipError5Digit: 'Por favor, ingrese un código ZIP válido de 5 dígitos.',
    zipErrorInvalid: 'Por favor, ingrese un código ZIP válido.',
    consentLabel: '¡Sí, envíenme por mensaje de texto una copia de mi cotización y ofertas adicionales de Ezpawn!',
    consentPrefix: (brandA) =>
      `Al marcar esta casilla, acepto recibir uno o más mensajes de texto relacionados con mi cotización de ${brandA} y su familia de tiendas al número de teléfono proporcionado. También acepto recibir mensajes de texto promocionales automatizados recurrentes, incluidas ofertas especiales, promociones y actualizaciones, de `,
    consentEzPlusStoresLink: 'EZ+ y Tiendas',
    consentMiddle:
      ' al número de teléfono proporcionado. Autorizo el uso de un sistema automatizado para enviar estos mensajes. El consentimiento no es una condición de ninguna compra o servicio. Envíe AYUDA al 81910 para obtener ayuda o ALTO para cancelar los mensajes de texto. La frecuencia de los mensajes varía. Se pueden aplicar tarifas de mensajes y datos. Consulte ',
    privacyPolicyLink: 'Política de privacidad',
    consentAnd: 'y',
    termsLink: 'Términos y condiciones',
    consentPost: '.',
    sending: 'Enviando...',
    verifyTitle: 'Verifique su número de teléfono',
    enterCode: 'Por favor, ingrese el código enviado a',
    didntGetCode: '¿No recibió un código?',
    resending: 'Reenviando...',
    resendCode: 'Reenviar código',
    phoneVerified: 'Número de teléfono verificado',
    emailTitle: 'Necesitamos su correo electrónico',
    emailDescription:
      'Le enviaremos una copia de esta cotización para que pueda consultarla más tarde.',
    emailLabel: 'Dirección de correo electrónico',
    emailPlaceholder: 'su@correo.com',
    optInPromotions: 'Suscribirse a promociones por correo electrónico',
    promotionsTextPrefix: 'Al marcar la casilla, acepta recibir mensajes de correo electrónico promocionales personalizados de ',
    promotionsTextLink: 'las subsidiarias y afiliadas de EZCORP®',
    promotionsTextSuffix:
      ' en la dirección de correo electrónico proporcionada. El consentimiento no es una condición de ninguna compra ni es necesario para acceder al sitio. Puede cancelar la suscripción a correos electrónicos promocionales en cualquier momento usando el enlace al final del correo. Consulte ',
    invalidEmail: 'Por favor, ingrese una dirección de correo electrónico válida.',
    failedEmail: 'No se pudo enviar el correo. Por favor, inténtelo de nuevo.',
    somethingWrong: 'Algo salió mal. Por favor, inténtelo de nuevo.',
    verifying: 'Verificando...',
    failedOtp: 'No se pudo enviar el código. Por favor, inténtelo de nuevo.',
    invalidOtp: 'Código inválido. Por favor, inténtelo de nuevo.',
    emailSent: '¡Correo enviado!',
    send: 'Enviar',
    tncPre: 'Para más información, consulte nuestros',
    termsOfUse: 'Términos de uso',
    tncMid: 'y',
    privacyPolicy: 'Política de privacidad.',
  },

  quoteModal: {
    title: '¿Qué impacta el valor ofrecido?',
    intro:
      'Nos esforzamos por ofrecer precios justos por sus artículos. Sin embargo, hay algunas razones clave por las que ofrecemos menos efectivo de lo que el producto podría valer en una tienda minorista:',
    resaleRisk:
      'Riesgo de reventa: Cuando compramos un artículo, existe el riesgo de que tarde en revenderse o que no se venda en absoluto. La oferta que hacemos tiene en cuenta esta incertidumbre.',
    operatingCosts:
      'Costos operativos: Como cualquier negocio, tenemos costos generales, como personal, operaciones de tienda y seguridad. Esto influye en el precio que podemos pagar mientras seguimos operando.',
    marketDemand:
      'Demanda del mercado: El valor de reventa de los artículos depende en gran medida de las tendencias del mercado y la demanda. Algunos artículos pueden mantener su valor, mientras que otros se deprecian con el tiempo. Basamos nuestras ofertas en la demanda actual del mercado.',
  },

  introModal: {
    title: 'Herramienta de cotización instantánea',
    describeItem: 'Describa su artículo',
    describeItemDesc: 'Le guiaremos a través de los detalles.',
    provideContact: 'Proporcione su número de teléfono y código ZIP',
    provideContactDesc: 'Necesario para generar la cotización.',
    getQuoteInstantly: 'Obtenga su cotización al instante',
    getQuoteInstantlyDesc: 'Sin necesidad de conducir y esperar en filas.',
    note: 'Puede agregar hasta 3 artículos por cotización.',
    getStarted: 'Comenzar',
  },

  category: {
    itemType: 'Tipo de artículo',
    selectOption: 'Seleccione una opción',
    weight: 'peso',
    karat: 'quilates',
    purity: 'pureza',
    goldInfoPre: 'Su estimación se basará en',
    goldInfoAnd: 'y',
    goldInfoPost: 'por ahora. Podemos confirmar el resto en persona.',
    cantFindNote:
      '¿No encuentra lo que busca? ¡Agregaremos más categorías en el futuro!',
    visitStore: 'O visite una tienda para una cotización en persona',
  },

  attributes: {
    searchPlaceholder: (attrName) => `Buscar ${attrName}`,
    warningMessage:
      'No podremos proporcionarle una estimación precisa sin esta información.',
    errorMustSelect: (attrName) => `Debe seleccionar ${attrName} para continuar`,
    displayName: (name) => {
      const map: Record<string, string> = {
        Quality: 'Pureza',
        Purity: 'Pureza',
        Manufacturer: 'Fabricante',
        'Model Number': 'Número de modelo',
        'Processor Type': 'Tipo de procesador',
        'Internal Storage Capacity (GB)': 'Almacenamiento interno (GB)',
        'Internal Storage Capacity': 'Almacenamiento interno',
        'Internal Storage': 'Almacenamiento interno',
        'RAM Memory (GB)': 'Memoria RAM (GB)',
        'RAM Memory': 'Memoria RAM',
        Karat: 'Quilates',
        Condition: 'Condición',
        Diamonds: 'Diamantes',
        'Item Type': 'Tipo de artículo',
      };
      return map[name] ?? name;
    },
  },

  finalReview: {
    totalEstimate: 'Cotización total estimada',
    quoteId: (id) => `ID de cotización: ${id}`,
    howDerived: '¿Cómo se calculó esta cotización?',
    expertReviewNeeded: 'Se requiere revisión en tienda para algunos de sus artículos.',
    expertReviewGeneralMessage: (categoryNames, itemCount) =>
      itemCount > 1
        ? `Podemos ofrecerle un precio una vez que veamos los artículos. No pudimos valorar sus ${categoryNames} en línea, pero si están en buenas condiciones, le haremos una oferta. Visítenos y uno de nuestros representantes estará encantado de revisarlos más de cerca.`
        : `Podemos ofrecerle un precio una vez que veamos su artículo. No pudimos valorar su ${categoryNames} en línea, pero si está en buenas condiciones, le haremos una oferta. Visítenos y uno de nuestros representantes estará encantado de revisarlo más de cerca.`,
    expertReviewMultipleMessage:
      'Podemos ofrecerle un precio una vez que veamos los artículos. No pudimos valorar algunos de los artículos en línea, pero si están en buenas condiciones, le haremos una oferta. Visítenos y uno de nuestros representantes estará encantado de revisarlos más de cerca.',
    expertReviewJewelryMessage:
      'Podemos ofrecerle un precio una vez que veamos su artículo. El valor de la joyería depende del peso, el quilate y la pureza, y no contábamos con lo necesario para esta pieza. Visítenos y uno de nuestros representantes estará encantado de probarla con usted y hacerle una oferta.',
    diamondNote: (metalLabel) =>
      `Esta estimación se basa únicamente en el valor de reventa del ${metalLabel}. Los diamantes, piedras preciosas u otros materiales no están incluidos en la estimación y requieren verificación en tienda.`,
    conditionNote:
      'La condición del artículo, incluido si está roto, puede afectar el valor final evaluado en tienda.',
    whyInstore: '¿Por qué revisión en tienda?',
    modelsMissing: 'Pueden faltar detalles como números de modelo.',
    authenticity: 'Algunos artículos pueden necesitar verificación de autenticidad.',
    premiumMaterials: 'Es posible que necesitemos verificar materiales premium.',
    emailCopy: 'Enviarme una copia por correo',
    emailLimit: 'Ya ha enviado esta cotización por correo el número máximo de veces.',
    quoteDetailsBtn: 'Detalles de la cotización',
    bringItems: 'Lleve sus artículos a una de nuestras tiendas para una revisión detallada:',
    storesNear: (zipCode) => `Tiendas cerca de ${zipCode}`,
    storeClosestToYou: 'Tienda más cercana a usted',
    getDirections: 'Cómo llegar',
    findAnotherStore: 'Encontrar otra tienda',
    quoteDetailsHeading: 'Detalles de la cotización',
    startNewQuote: 'Iniciar una nueva cotización',
  },

  summary: {
    addAnotherItem: 'Agregar otro artículo',
    maxItems: 'Ha alcanzado el número máximo de artículos para esta cotización.',
    canAddPre: 'Puede agregar',
    canAddMid: (remaining) => (remaining === 1 ? 'artículo más' : 'artículos más'),
    canAddPost: 'a esta cotización.',
    getMyQuote: 'Obtener mi cotización',
  },

  ezquote: {
    sessionExpired:
      'Su sesión expiró por inactividad\n¡Obtengamos una nueva cotización!',
    bestCondition: 'Condición óptima',
    bestConditionDesc: 'Como nuevo, mínimas señales de uso',
    goodCondition: 'Buena condición',
    goodConditionDesc: 'Ligeramente usado, desgaste visible menor',
    fairCondition: 'Condición regular',
    fairConditionDesc: 'Desgaste notable pero completamente funcional',
    errorSelectCategory: 'Por favor, seleccione una categoría.',
    errorSelectCondition: 'Por favor, seleccione una condición.',
    invalidStep: 'Paso inválido',
  },

  savedQuote: {
    notFoundMessage: 'No pudimos encontrar una cotización con ese ID. Puede que haya expirado o que el enlace sea incorrecto.',
  },

  headers: {
    step2Title: 'Seleccione la categoría de su artículo',
    step2Subtitle: 'Elija la categoría que mejor describe su artículo.',
    step3Title: 'Precisemos más',
    step3Subtitle: 'Por favor, proporcione los siguientes detalles de su artículo.',
    step4Title: '¿Cuál es la condición de su artículo?',
    step4Subtitle: 'Elija la condición que mejor describe su artículo.',
    step5Title: 'Aquí hay un resumen de sus artículos',
    step5Subtitle: 'Estimaremos su valor en el siguiente paso.',
    step6Title: 'Aquí está su cotización estimada',
    step6Subtitle:
      'Le recomendamos que visite la tienda pronto ya que los valores pueden cambiar con el tiempo.',
    goldNarrowTitle: 'Precisemos más',
    goldNarrowSubtitle: 'Por favor, seleccione qué tipo de artículo tiene.',
    goldWeightTitle: '¿Cuál es el peso estimado de su artículo?',
    goldWeightSubtitle:
      'Una estimación aproximada es suficiente. Cuanto más precisa sea su estimación, más exacta será su cotización.',
    goldKaratTitle: '¿Conoce los quilates?',
    goldKaratSubtitle: 'Por favor, seleccione el valor de quilates del oro.',
    goldPurityTitle: '¿Conoce la pureza?',
    goldPuritySubtitle: 'Por favor, seleccione el valor de pureza.',
    goldDetailsTitle: '¿Hay otros detalles que le gustaría que consideremos?',
    goldDetailsSubtitle:
      'Puede incluir información como la marca, detalles únicos y más.',
    goldDiamondsTitle: '¿Su artículo tiene diamantes o piedras preciosas?',
    goldDiamondsSubtitle: 'Elija la opción que mejor describe su artículo.',
  },
};

export default es;
